import React from 'react';
import { CheckIcon, Loader2Icon, UsersIcon } from 'lucide-react';
import { RecipientCard } from './RecipientCard';
import { ReadinessCard } from './ReadinessCard';
import { ComplianceReview } from './ComplianceReview';
import { IntegrationErrorCard } from './IntegrationErrorCard';
import { StatusPill } from './ui/StatusPill';
import type { BorrowerPair, IntegrationError, PackageState, Recipient, ValidationState } from '../types/disclosures';
interface PrepareStepProps {
  pairs: BorrowerPair[];
  activePair: BorrowerPair;
  activePairId: string;
  onSelectPair: (pairId: string) => void;
  validation: Record<string, ValidationState>;
  packages: Record<string, PackageState>;
  activeValidation: ValidationState;
  activeError: IntegrationError | null;
  onGenerate: () => void;
  onGenerateAll: () => void;
  onRetryValidation: () => void;
  onEditRecipient: (recipient: Recipient) => void;
  onErrorAction: (error: IntegrationError) => void;
}
export function PrepareStep({
  pairs,
  activePair,
  activePairId,
  onSelectPair,
  validation,
  packages,
  activeValidation,
  activeError,
  onGenerate,
  onGenerateAll,
  onRetryValidation,
  onEditRecipient,
  onErrorAction
}: PrepareStepProps) {
  const multiplePairs = pairs.length > 1;
  const allReady = pairs.every(pair => validation[pair.id]?.phase === 'passed');
  const showComplianceCard = activeValidation.phase === 'passed' || activeValidation.complianceFindings.length > 0;
  const blockingError = activeError && (activeValidation.phase !== 'issues' || activeError.kind === 'no_package');
  return <div className="space-y-4" data-id="element-171" data-mp="10ry65z5w4qrk">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-card" data-id="element-172" data-mp="10r6l3i9x6bfj">
        <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-slate-900" data-id="element-173" data-mp="vteo49uf3z4h">
          Prepare Initial Disclosures
        </h2>
        <p className="mt-1 text-[13px] leading-5 text-slate-500" data-id="element-174" data-mp="3ljrz1my7iry9">
          Review the recipients and loan information before generating the disclosure package.
        </p>

        {multiplePairs && <div className="mt-5" data-id="element-175" data-mp="3i12m0v2g9sua">
            <div className="flex items-center justify-between gap-3" data-id="element-176" data-mp="3mzcg12cfuneb">
              <p className="text-[12px] font-medium uppercase tracking-wide text-slate-500" data-id="element-177" data-mp="3mykuylggw82a">
                Borrower Pair
              </p>
              <button type="button" onClick={onGenerateAll} disabled={!allReady} className="text-[12px] font-semibold text-blue-700 transition hover:text-blue-800 disabled:cursor-not-allowed disabled:text-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-178" data-mp="3d0nb0a5n4xfh">
                Generate All Packages
              </button>
            </div>
            <div className="mt-2 inline-flex w-full flex-col gap-1 rounded-lg bg-slate-100 p-1 sm:flex-row" data-id="element-179" data-mp="2ub89kem1m6ak">
              {pairs.map(pair => {
            const state = validation[pair.id];
            const isActive = pair.id === activePairId;
            return <button key={pair.id} type="button" onClick={() => onSelectPair(pair.id)} aria-pressed={isActive} className={`flex flex-1 items-center justify-between gap-2 rounded-md px-3 py-2 text-[13px] font-medium transition ${isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-180" data-mp="21pa03uu6hv8n">
                    <span className="truncate" data-id="element-181" data-mp="2flvrcmzjglu3">{pair.label}</span>
                    {packages[pair.id]?.status === 'sent' ? <StatusPill tone="success" data-id="element-182" data-mp="2pe1la29cpdbx">Sent</StatusPill> : packages[pair.id]?.status === 'ready' ? <StatusPill tone="info" data-id="element-183" data-mp="33elz0k0esiyk">Generated</StatusPill> : state?.phase === 'running' ? <Loader2Icon className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" data-id="element-184" data-mp="2ygat2kd8islm" /> : state?.phase === 'passed' ? <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-emerald-700" data-id="element-185" data-mp="11nv9o4zky00z">
                        <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-186" data-mp="16gep77htv58f" />
                        Ready
                      </span> : <span className="shrink-0 text-[12px] font-medium text-amber-700" data-id="element-187" data-mp="dqplunhnat3n">
                        Needs review
                      </span>}
                  </button>;
          })}
            </div>
          </div>}

        <div className="mt-5" data-id="element-188" data-mp="mt8l6xm82nl5">
          <div className="flex items-center gap-2" data-id="element-189" data-mp="rqqnw69nc6kp">
            <UsersIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-190" data-mp="rriknkezcack" />
            <h3 className="text-[13px] font-semibold text-slate-900" data-id="element-191" data-mp="1y5humqlgalmn">
              {multiplePairs ? 'Disclosure Recipients' : `Recipients · ${activePair.label}`}
            </h3>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-192" data-mp="2d7zrlzcsprza">
            {activePair.recipients.map(recipient => <RecipientCard key={recipient.id} recipient={recipient} onEdit={onEditRecipient} data-id="element-193" data-mp="28azwgjhhi9my" />)}
          </div>
        </div>
      </section>

      {blockingError ? <IntegrationErrorCard error={activeError} onAction={() => onErrorAction(activeError)} data-id="element-194" data-mp="1uijrhd50w1je" /> : <ReadinessCard validation={activeValidation} canGenerate={activeValidation.phase === 'passed'} onGenerate={onGenerate} onRetryValidation={onRetryValidation} onIssueAction={() => onRetryValidation()} data-id="element-195" data-mp="1pj72alqoiidn" />}

      {showComplianceCard && !blockingError && <ComplianceReview findings={activeValidation.complianceFindings} data-id="element-196" data-mp="3exdb15aa2jtq" />}
    </div>;
}